/**
 * IMPORTANT: This component should only be loaded through the CheckoutWithStripe wrapper.
 * Do not import or use this component directly - always use CheckoutWithStripe instead.
 *
 * The CheckoutWithStripe wrapper handles:
 * - Stripe.js lazy loading with retry logic
 * - Ad-blocker detection and error handling
 * - Providing the required stripe prop to this component
 *
 * @see CheckoutWithStripe.js
 */
import isAutoBillingAddon from '../utils/is-auto-billing-addon.ts';
import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import {
    getCachedBillingData,
    getPartialBillingData,
    getBillingData,
    fetchPaymentMethod,
    changeOrActivateSubscriptionPlan,
    getInvoicePreview,
    getForceUpgrade,
    getLatestInvoice,
} from '../data/api';
import { logError, setContext } from '../utils/sentry-logger';
import formatNumber from '../utils/format-number';
import formatPeriod from '../utils/format-period';
import { monthlyPrice, yearlyPrice, getPriceForPeriod } from '../utils/price-for-period';
import { findNearestLimit, findSuitablePrices } from '../utils/member-limits';
import checkLimits from '../utils/check-limits';
import {
    getMembersIncluded,
    findComparablePrice,
    getTotalPrice,
    parseProductIdToStripeId,
    createProductIdFromStripeId,
} from '../utils/billing-utils';
import {
    ensureCorrectSignupCouponForPeriod,
    determineSignupCouponForPeriod,
    createExpectedDiscount,
    determineSignupCouponsToFetch,
    fetchAlternateSignupCoupon,
    getCouponDiscount,
} from '../utils/coupon-utils';
import { sleep } from '../utils/common';
import { hasActiveSubdirectory } from '../utils/site-utils';

import SpinnerButton from './shared/SpinnerButton';
import InvoiceDetailsModal from './shared/InvoiceDetailsModal';
import UpdateCardModal from './stripe/UpdateCardModal';
import AddonItem from './Checkout/AddonItem';
import AnnualDiscount from './Checkout/AnnualDiscount';
import CouponSection from './Checkout/CouponSection';
import PeriodPrice from './Checkout/PeriodPrice';
import PeriodToggle from './Checkout/PeriodToggle';
import Loading from './shared/Loading';
import { envToBoolean } from '../utils/env';
import {
    WEBHOOK_PROCESSING_DELAY_MS,
    AUTO_PRICE_REMOVAL_DELAY_MS,
    GHOST_RESTART_DELAY_MS,
    MEMBER_MIN_COUNT,
    ROUTES,
    BILLING_PERIODS,
    SENDING_DOMAIN_STATES,
    SUBDIRECTORY_ADDON_NAME,
} from '../utils/constants';

/**
 * @typedef {import('../types').BillingData} BillingData
 * @typedef {import('../utils/ProductUtils').Addon} Addon
 * @typedef {{selectedProductId: string, selectedPeriod: string}} CheckoutParams
 */

const DEFAULT_ERROR_MESSAGE =
    'Something went wrong. Refresh the page and try again or contact support@ghost.org';

/**
 * Builds the current/selected addon price ID pairs for the invoice preview.
 * @param {object} data
 * @param {Addon[]} [data.selectedAddons]
 * @param {Addon | null} [data.autoPriceAddon]
 * @param {string} [data.autoPriceId] the currently active auto price ID
 * @param {'month'|'year'} data.period
 * @returns {Array<{selected?: string, current?: string}>}
 */
function buildAddonPrices({ selectedAddons, autoPriceAddon, autoPriceId, period }) {
    const addonPrices = [];

    (selectedAddons || []).forEach((selectedAddon) => {
        const addon = {};
        // Ghost supports exactly 2 billing periods (month/year), so the else clause
        // correctly sets addon.current to the opposite period without overwriting
        selectedAddon.prices.forEach((addonPrice) => {
            if (addonPrice.billing_period === period) {
                addon.selected = addonPrice.stripe_price_id;
            } else {
                addon.current = addonPrice.stripe_price_id;
            }
        });

        addonPrices.push(addon);
    });

    if (autoPriceAddon) {
        addonPrices.push({
            current: autoPriceId,
            selected: autoPriceAddon.prices.find((price) => price.billing_period === period)
                ?.stripe_price_id,
        });
    }

    return addonPrices;
}

class Checkout extends React.Component {
    _verbose = envToBoolean(process.env.REACT_APP_VERBOSE);
    _isMounted = false;

    buttonStates = {
        fail: {
            className: 'gh-btn gh-btn-block',
            html: 'Try again',
            onSubmit: (e) => this.handleConfirmAndSubmit(e),
            disabled: false,
        },
        pending: {
            className: 'gh-btn gh-btn-green gh-btn-block spinner',
            html: '<span><div class="gh-spinner"></div></span>',
            disabled: true,
        },
        default: {
            className: 'gh-btn gh-btn-green gh-btn-block',
            html: 'Confirm plan selection',
            onSubmit: (e) => this.handleConfirmAndSubmit(e),
            disabled: false,
        },
        scaRetry: {
            className: 'gh-btn gh-btn-block',
            html: 'Complete payment now',
            onSubmit: (e) => this.handleConfirmAndSubmit(e),
            disabled: false,
        },
    };

    constructor(props) {
        super(props);

        if (props) {
            props.showNav(false);
        }

        // initial state
        this.state = {
            suitablePrice: null,
            selectedAddons: [],
            availableAddons: [],
            comparablePrice: null,
            subscription: null,
            period: null,
            loading: true,
            error: null,
            amountDue: 0,
            endingBalance: 0,
            updateCardModalIsOpen: false,
            invoiceDetailsModalIsOpen: false,
            buttonState: this.buttonStates.default,
            btnDisabled: false,
            isSCARetry: false,
            isPendingOrIncompletePrice: false,
            pendingCouponUpdate: null,
            couponLoading: false,
            expectedDiscount: null,
            fetchedCoupons: {}, // Store fetched coupon details by ID
        };

        // Bind only the methods that are passed down as callbacks
        this.togglePeriod = this.togglePeriod.bind(this);
        this.toggleAddonDetails = this.toggleAddonDetails.bind(this);
        this.handleAddonAdded = this.handleAddonAdded.bind(this);
        this.handleAddonRemoved = this.handleAddonRemoved.bind(this);
        this.handleOpenUpdateCardModal = this.handleOpenUpdateCardModal.bind(this);
        this.handleCloseModal = this.handleCloseModal.bind(this);
        this.handleOpenInvoiceDetailsModal = this.handleOpenInvoiceDetailsModal.bind(this);
        this.handleNestedLoadingState = this.handleNestedLoadingState.bind(this);
    }

    /**
     * Parses the provided query parameters and matches it to a valid product and period.
     * @param {string} param
     * @returns {CheckoutParams | null}
     */
    parseCheckoutParamsToProductAndPeriod(param) {
        if (!param) {
            return null;
        }

        // Ignore personal and trial plans
        // Depending on environment, trial plans can be named either 'trial' or 'personal'
        const isTrial = param.indexOf('personal') >= 0 || param.indexOf('trial') >= 0;

        if (isTrial) {
            return null;
        }

        const paramsRegex = new RegExp(
            `^([A-Za-z0-9]+)-(${BILLING_PERIODS.MONTH}|${BILLING_PERIODS.YEAR})$`,
        );
        const parsedParam = paramsRegex.exec(param);

        if (!parsedParam) {
            return null;
        }

        try {
            const [, productId, period] = parsedParam;
            const fullStripeId = parseProductIdToStripeId(productId);

            return {
                selectedProductId: fullStripeId,
                selectedPeriod: period,
            };
        } catch (error) {
            logError(error, { level: 'warning' });
            return null;
        }
    }

    /**
     *
     * @param {BillingData} billingData
     * @param {{selectedProductId: string, selectedPeriod: string} | null} params
     * @returns
     */
    async validateInitialState(billingData, params) {
        // Parse and validate checkout parameters
        if (!params) {
            this.props.history.push(ROUTES.DASHBOARD);
            return false;
        }

        // Early return when user is in grace period (needs card update)
        if (billingData.isGrace) {
            this.props.history.push(ROUTES.DASHBOARD);
            return false;
        }

        return true;
    }

    shouldRedirectToDashboard(billingData) {
        const hasCustomProduct =
            billingData?.currentPrice?.base_product?.toLowerCase() === 'custom';
        const notIncompleteOrAutoBillingGrace =
            !billingData?.isIncomplete && !billingData?.isAutoBillingGrace;
        const hasErrorOrNoBillingData = !billingData || billingData?.error;
        const noAvailableProducts =
            !billingData?.availableProducts || !billingData?.availableProducts?.length;

        return (
            notIncompleteOrAutoBillingGrace &&
            (hasErrorOrNoBillingData || noAvailableProducts || hasCustomProduct)
        );
    }

    async fetchInitialCoupons(discount) {
        let fetchedCoupons = {};
        let couponError = null;

        if (discount?.coupon?.id) {
            const { fetchedCoupons: initialCoupons, couponToFetch } =
                determineSignupCouponsToFetch(discount);
            const result = await fetchAlternateSignupCoupon(couponToFetch, initialCoupons);
            fetchedCoupons = result.fetchedCoupons;
            couponError = result.error ? DEFAULT_ERROR_MESSAGE : null;
        }

        return { fetchedCoupons, couponError };
    }

    getSubdirectoryAddons(hasSubdirectory, selectedBaseProduct) {
        const subdirectoryAddons = [];

        if (hasSubdirectory && selectedBaseProduct?.addons?.length) {
            if (this._verbose) {
                console.log(
                    `[CHECKOUT] Subdirectory addon available in ${selectedBaseProduct?.name} product.`,
                );
            }
            const subdirectoryAddon = selectedBaseProduct.addons.find(
                (addon) => addon.name === SUBDIRECTORY_ADDON_NAME,
            );
            if (subdirectoryAddon) {
                subdirectoryAddons.push(subdirectoryAddon);
            }
        }

        return subdirectoryAddons;
    }

    /**
     * @param {boolean} hasAutoBillingEnabled
     * @param {import('../types').ProductWithPricesAndFeatures} selectedBaseProduct
     * @returns {Addon|null|undefined}
     */
    getAutoPriceAddon(hasAutoBillingEnabled, selectedBaseProduct) {
        let autoPriceAddon = null;

        if (hasAutoBillingEnabled && selectedBaseProduct?.addons?.length) {
            // Customer has auto price add on. We need to add it to the selected addons
            if (this._verbose) {
                console.log(
                    `[CHECKOUT] Auto price addon available and added for ${selectedBaseProduct?.name} product.`,
                );
            }

            autoPriceAddon = selectedBaseProduct.addons.find(isAutoBillingAddon);
        }

        return autoPriceAddon;
    }

    async componentDidMount() {
        this.props.updateTitle('Confirm your plan selection');
        this._isMounted = true;

        // Parse and validate checkout parameters
        const params = this.parseCheckoutParamsToProductAndPeriod(this.props.match.params.id);

        const billingData = await getCachedBillingData();

        if (!(await this.validateInitialState(billingData, params))) {
            return;
        }

        /** @type {CheckoutParams} */
        const { selectedProductId, selectedPeriod } = params;

        let suitablePrice;
        let limitsValidations = [];

        this.setState({
            selectedProductId,
            period: selectedPeriod,
            buttonState: { ...this.buttonStates.default, disabled: true },
        });

        setContext('checkout', {
            current_price: billingData?.currentPrice?.nickname,
            selected_product_id: selectedProductId,
            selected_period: selectedPeriod,
            site: billingData?.currentSite?.url,
        });

        // Ensure we always load fresh limit data from Daisy in case anything has changed (e. g. theme or number of staff)
        const currentSiteLimits = await getPartialBillingData('currentSiteLimits');

        let discount = billingData?.discount;
        const { fetchedCoupons, couponError } = await this.fetchInitialCoupons(discount);

        if (this._isMounted) {
            const hasCard = billingData?.user?.cardType;
            let buttonState = { ...this.buttonStates.default, disabled: !hasCard || !!couponError };
            let baseProductAddons = [];
            let selectedAddons = [];
            let autoPriceAddon = null;
            const hasSubdirectory = hasActiveSubdirectory(billingData?.currentSite);
            const hasCustomSendingDomain =
                billingData?.currentSite?.sending_domain_status === SENDING_DOMAIN_STATES.VERIFIED;

            if (this.shouldRedirectToDashboard(billingData)) {
                return this.props.history.push(ROUTES.DASHBOARD);
            }

            const selectedBaseProduct = billingData?.availableProducts?.find((product) => {
                return product.stripeId === selectedProductId;
            });

            const entryProduct = billingData?.availableProducts?.[0]?.name?.toLowerCase();

            if (!selectedBaseProduct) {
                if (this._verbose) {
                    console.log(
                        "[CHECKOUT] Product not available or doesn't exist:",
                        selectedProductId,
                    );
                }
                return this.props.history.push(ROUTES.PLANS);
            }

            // We do not support changing to the entry product if the site has a valid custom sending domain
            const isSelectingEntryProduct =
                selectedBaseProduct?.name?.toLowerCase() === entryProduct;

            if (
                hasCustomSendingDomain &&
                isSelectingEntryProduct &&
                billingData?.currentPrice?.base_product.toLowerCase() !== entryProduct
            ) {
                return this.props.history.push(ROUTES.PLANS);
            }

            const subdirectoryAddons = this.getSubdirectoryAddons(
                hasSubdirectory,
                selectedBaseProduct,
            );
            baseProductAddons.push(...subdirectoryAddons);

            autoPriceAddon = this.getAutoPriceAddon(
                billingData?.hasAutoBillingEnabled,
                selectedBaseProduct,
            );

            const currentMembers =
                billingData?.currentSiteLimits?.members?.total || MEMBER_MIN_COUNT;
            const memberLimit = findNearestLimit(currentMembers, billingData?.availableProducts);

            if (baseProductAddons && hasSubdirectory) {
                if (this._verbose) {
                    console.log('[CHECKOUT] Subdirectory addon required');
                }
                // pre-select the subdirectory add-on, if the current site has a subdirectory enabled
                selectedAddons = baseProductAddons;
            }

            if (this._verbose) {
                console.log('[CHECKOUT] Selected base product: ', selectedBaseProduct?.name);
            }

            let suitablePrices = findSuitablePrices(
                selectedBaseProduct?.prices,
                memberLimit,
                selectedPeriod,
            );

            if (suitablePrices && suitablePrices.length && currentSiteLimits) {
                // The prices are already sorted by member limits, so we can safely pick the first one
                suitablePrice = suitablePrices[0];

                // Check any suitable price against limits that would affect the price
                limitsValidations = checkLimits(suitablePrice, {
                    currentSiteLimits,
                    product: selectedBaseProduct?.name,
                    productLimits: selectedBaseProduct?.limits,
                    verbose: this._verbose,
                });
            }

            // Return to plans if we couldn't find a suitable price in the current product
            if (
                !suitablePrices ||
                !suitablePrices.length ||
                (limitsValidations && limitsValidations.length)
            ) {
                return this.props.history.push(ROUTES.PLANS);
            }

            if (this._verbose) {
                console.log(
                    `[CHECKOUT] Suitable price for ${currentMembers} members: `,
                    suitablePrice,
                );
            }

            let showAddonStates = {};

            if (baseProductAddons?.length) {
                // set the details toggle to closed by default
                baseProductAddons.forEach(({ name }) => (showAddonStates[name] = false));
            }

            const comparablePrice = findComparablePrice(selectedBaseProduct.prices, suitablePrice);

            // For convenience reasons, add the members limit as a property to state
            const membersIncluded = getMembersIncluded(suitablePrice);

            // Check if we need to display the expected discount on initial load
            let expectedDiscount = null;
            let pendingCouponUpdate = null;

            if (discount && selectedPeriod) {
                const { couponId, needsUpdate } = determineSignupCouponForPeriod(
                    selectedPeriod,
                    discount,
                );
                if (needsUpdate && couponId) {
                    expectedDiscount = createExpectedDiscount(discount, couponId, fetchedCoupons);
                    pendingCouponUpdate = couponId;
                }
            }

            // Calculate initial amountDue for incomplete/pending subscriptions from invoice
            let initialAmountDue = 0;
            if (
                (billingData.isIncomplete || billingData.isPending) &&
                this.hasValidLatestInvoice()
            ) {
                initialAmountDue = billingData.latestInvoice.amount_remaining / 100;
            }

            // We got all the information we need to start with. Let's set it in state
            this.setState({
                ...billingData,
                discount,
                originalDiscount: discount, // Store the original discount for API calls
                expectedDiscount,
                pendingCouponUpdate,
                fetchedCoupons, // Store the fetched coupon data
                _invoiceDetails: billingData.invoiceDetails,
                selectedBaseProduct,
                availableAddons: baseProductAddons || [],
                suitablePrice,
                comparablePrice,
                autoPriceAddon,
                amountDue: initialAmountDue,
                showAddon: showAddonStates,
                selectedAddons,
                membersIncluded,
                period: suitablePrice.billing_period,
                loading: false,
                buttonState,
                hasSubdirectory,
                error: couponError
                    ? DEFAULT_ERROR_MESSAGE
                    : !hasCard
                      ? 'Add a card to continue'
                      : null,
            });

            // If we have detected a pending subscription, check if we're in the current price and
            // which state it is (incomplete or pending)
            if (billingData.pendingSubscription) {
                const scaRetryState = { isSCARetry: true };

                // A subscription in an incomplete state should not be able to change around anymore.
                // We need to disable all other buttons and clearly signal to finalise the payment with SCA.
                if (billingData.isIncomplete || billingData.isAutoBillingGrace) {
                    // For incomplete subscriptions, use the actual invoice amount instead of recalculating
                    if (this.hasValidLatestInvoice()) {
                        scaRetryState.amountDue = billingData.latestInvoice.amount_remaining / 100;
                    } else {
                        // Fallback to calculation if no invoice data
                        scaRetryState.amountDue = getTotalPrice({
                            cost: this.state?.suitablePrice?.cost,
                            period: this.state.period,
                            selectedAddons: this.state?.selectedAddons,
                        });
                    }
                    scaRetryState.btnDisabled = true;

                    this.setState(scaRetryState);

                    // Change the URL parameter to reflect the new selection if we need to
                    if (window.location.pathname !== this.state.checkoutRoute) {
                        return this.props.history.push(`/checkout/${this.state.checkoutRoute}`);
                    }
                }

                // Check if we're on the exact pending subscription plan
                if (this.isOnPendingOrIncompletePrice()) {
                    this.props.updateTitle('Complete payment authorization');

                    buttonState = this.buttonStates.scaRetry;

                    // For pending subscriptions (paid to paid), also use invoice amount if available
                    if (billingData.isPending && this.hasValidLatestInvoice()) {
                        scaRetryState.amountDue = billingData.latestInvoice.amount_remaining / 100;
                    }

                    this.setState({ ...scaRetryState, isPendingOrIncompletePrice: true });
                }

                this.setPendingOrIncompletePrice();
            }

            if (
                !this.state.isPendingOrIncompletePrice &&
                this.state.suitablePrice?.stripe_price_id ===
                    this.state?.currentPrice?.stripe_price_id
            ) {
                return this.setState({
                    error: 'This is your current plan. Choose a different period or plan.',
                    buttonState: { ...this.buttonStates.default, disabled: true },
                });
            } else if (
                !billingData.isIncomplete &&
                !billingData.isAutoBillingGrace &&
                !billingData.isPending
            ) {
                this.setState({
                    // Disable the checkout button from the start if the user
                    // has no card or we failed to update the coupon on mount.
                    buttonState: {
                        ...this.buttonStates.default,
                        disabled: !billingData?.user?.cardType || !!couponError,
                    },
                });
            }

            await this.getAmountDue(suitablePrice.stripe_price_id, suitablePrice.billing_period);
        }
    }

    componentWillUnmount() {
        this.props.updateTitle('Billing and domain settings');
        this.props.showNav(true);
        this._isMounted = false;
    }

    handleOpenUpdateCardModal(value) {
        this.setState({ updateCardModalIsOpen: value });
    }

    handleOpenInvoiceDetailsModal(value) {
        this.setState({ invoiceDetailsModalIsOpen: value });
    }

    async handleCloseModal(options = {}) {
        const reload = options?.reload || false;
        const delay = options?.delay || 0;

        this.setState({
            updateCardModalIsOpen: false,
            invoiceDetailsModalIsOpen: false,
        });

        if (reload && reload === true) {
            this.props.showNav(false);

            this.setState({
                loading: true,
                error: null,
                buttonState: this.buttonStates.pending,
                btnDisabled: false,
            });

            try {
                const billingData = await getBillingData();

                this.setState({
                    ...billingData,
                    originalDiscount: billingData?.discount,
                    _invoiceDetails: billingData.invoiceDetails,
                    buttonState: {
                        ...this.buttonStates.default,
                        disabled: !billingData?.user?.cardType,
                    },
                    error: null,
                });

                const currentPrice = await getPartialBillingData('currentPrice');

                const newState = {
                    ...currentPrice,
                    loading: false,
                };

                await sleep(delay);

                this.setState(newState);
            } catch (error) {
                logError(error, {
                    level: 'error',
                    tags: {
                        shown_to_user: true,
                    },
                });

                return this.setState({
                    error: DEFAULT_ERROR_MESSAGE,
                    buttonState: this.buttonStates.fail,
                    loading: false,
                });
            }
        } else {
            try {
                const billingData = await getCachedBillingData();

                await sleep(delay);

                this.setState((state) => ({
                    ...billingData,
                    originalDiscount: billingData?.discount,
                    _invoiceDetails: billingData.invoiceDetails,
                    buttonState: state.isPendingOrIncompletePrice
                        ? this.buttonStates.scaRetry
                        : { ...this.buttonStates.default, disabled: !billingData?.user?.cardType },
                    error:
                        state.error &&
                        state.error.match(/add a card/i) &&
                        !billingData?.user?.cardType
                            ? state.error
                            : null,
                }));
            } catch (error) {
                logError(error, {
                    level: 'error',
                    tags: {
                        shown_to_user: true,
                    },
                });

                return this.setState({
                    error: DEFAULT_ERROR_MESSAGE,
                    buttonState: this.buttonStates.fail,
                    loading: false,
                });
            }
        }
    }

    // Check if we're currently on the exact pending/incomplete price
    isOnPendingOrIncompletePrice() {
        return (
            this.state.pendingSubscription?.priceId === this.state.suitablePrice?.stripe_price_id ||
            ((this.state.isIncomplete || this.state.isAutoBillingGrace) &&
                this.state?.pendingSubscription?.productId ===
                    this.state?.selectedBaseProduct?.stripeId &&
                this.state?.pendingSubscription?.period === this.state?.period)
        );
    }

    // Check if we have a valid latest invoice with amount remaining
    hasValidLatestInvoice() {
        return (
            this.state?.latestInvoice?.amount_remaining !== undefined &&
            this.state?.latestInvoice?.status === 'open'
        );
    }

    // Check if we should use the latest invoice data for amount calculations
    shouldUseLatestInvoice() {
        return this.isOnPendingOrIncompletePrice() && this.hasValidLatestInvoice();
    }

    // A pending update subscription can be overwritten by choosing a different plan and might expire without
    // taking effect. The customer needs to be able to switch to a different product, or period, but we still
    // need to notify them to finalise the payment in order to complete the purchase.
    // We only wanna do this notification when we're currently on the correct plan and period that the
    // customer left off when leaving the screen after a failed SCA.
    setPendingOrIncompletePrice() {
        if (this.isOnPendingOrIncompletePrice()) {
            if (
                // Members amount changed between attempted trial -> sub conversion, so the price is not the correct one anymore.
                // Overwrite the current suitable price, to make sure we checkout the correct one.
                (this.state.isIncomplete || this.state.isAutoBillingGrace) &&
                this.state?.pendingSubscription?.productId ===
                    this.state?.selectedBaseProduct?.stripeId &&
                this.state?.pendingSubscription?.period === this.state?.period
            ) {
                const suitablePrice = this.state.selectedBaseProduct?.prices.find(
                    (price) => price.stripe_price_id === this.state.pendingSubscription?.priceId,
                );
                const membersIncluded = getMembersIncluded(suitablePrice);

                this.setState({
                    suitablePrice,
                    membersIncluded,
                });
            }
            return this.setState({
                isPendingOrIncompletePrice: true,
            });
        } else {
            return this.setState({
                isPendingOrIncompletePrice: false,
            });
        }
    }

    async handlePaymentSCA(
        customerId = this.state?.subscription?.customer_id || this.state?.user?.stripe_token,
        clientSecret,
        paymentMethodId,
    ) {
        let scaResult = {};

        // Safety guard to ensure we have the Stripe customer ID
        if (!customerId) {
            const { subscription, user } = await getBillingData();

            // There's no hope, can't proceed without the Stripe customer ID
            if (!subscription?.customer_id && !user?.stripe_token) {
                return this.props.history.push(ROUTES.DASHBOARD);
            }

            customerId = subscription?.customer_id || user?.stripe_token;
        }

        this.setState({
            buttonState: this.buttonStates.pending,
            error: null,
            btnDisabled: true,
        });

        try {
            if (!this.state.isSCARetry && clientSecret && paymentMethodId) {
                // This should only be done on-session and before any failure
                this.setState({ isSCARetry: false });

                // use the client secret to confirm the card payment on-session
                scaResult = await this.doSCAPayment(
                    clientSecret,
                    paymentMethodId,
                    this.props.stripe,
                );
            } else {
                if (!clientSecret || this.state.isPendingOrIncompletePrice) {
                    // Fetch client_secret to initiate SCA payment
                    const invoice = await getLatestInvoice({ customerId });

                    clientSecret = invoice?.payment_intent?.client_secret;
                }

                if (!paymentMethodId || this.state.isPendingOrIncompletePrice) {
                    // Fetch payment method to initiate SCA payment.
                    const paymentMethod = await fetchPaymentMethod({ customerId });
                    paymentMethodId = paymentMethod.paymentMethodId;
                }

                this.setState({ isSCARetry: false });

                scaResult = await this.doSCAPayment(
                    clientSecret,
                    paymentMethodId,
                    this.props.stripe,
                );
            }
        } catch (error) {
            logError(error, {
                level: 'error',
                tags: {
                    source: 'handlePaymentSCA',
                    shown_to_user: true,
                },
            });

            return this.setState({
                error: "We weren't able to process your payment. Try again or choose a different card.",
                buttonState: this.buttonStates.fail,
                isSCARetry: true,
                btnDisabled: false,
            });
        }

        // The payment SCA was a success, proceed back to Dashboard, but first
        // grab the updated subscription information
        if (scaResult && scaResult?.paymentIntent?.status === 'succeeded') {
            if (this._verbose) {
                console.time('[CHECKOUT] SCA success, delay fetching subscription');
            }

            try {
                // We're done with the checkout, don't need that anymore.
                window.localStorage.removeItem(`gh_checkout_${customerId}`);
            } catch (error) {
                logError(error, { level: 'warning' });
            }

            this.props.showNav(false);

            this.setState({
                loading: true,
                btnDisabled: true,
                pendingSubscription: null,
                isPendingOrIncompletePrice: false,
                isSCARetry: false,
                isIncomplete: false,
                isPending: false,
            });

            // Wait for a bit before fetching the updated subscription data.
            // This should give Stripe and Daisy enough time to process webhooks
            // and receive the updated subscription state after a customer was in
            // dunning.
            await sleep(WEBHOOK_PROCESSING_DELAY_MS);
            // force update the subscription information
            await getBillingData({ sca: 'succeeded', updateAdmin: true });
            await getPartialBillingData('currentProduct');
            await getPartialBillingData('invoices', { userId: this.state?.user?.id });
            await getPartialBillingData('currentSite', { data: this.state.blogs });

            if (this._verbose) {
                console.timeEnd('[CHECKOUT] SCA success, delay fetching subscription');
            }
            await this.handleCloseModal({ reload: false });

            this.props.updateTitle('Billing and domain settings');
            this.props.showNav(true);

            return this.props.history.replace(ROUTES.DASHBOARD, {});
        } else {
            const billingData = await getBillingData();

            this.setState({
                ...billingData,
                error: "We weren't able to process your payment. Try again or choose a different card.",
                buttonState: this.buttonStates.fail,
                isSCARetry: true,
            });

            if (billingData?.isIncomplete || billingData.isAutoBillingGrace) {
                return this.setState({ btnDisabled: true });
            } else if (billingData?.isPending) {
                return this.setState({ btnDisabled: false });
            }
        }
    }

    // This will trigger the SCA modal to confirm the card payment on-session
    async doSCAPayment(clientSecret, paymentMethodId, stripe) {
        // confirm the card payment
        return await stripe.confirmCardPayment(clientSecret, {
            payment_method: paymentMethodId,
        });
    }

    handleNestedLoadingState(loading) {
        this.setState({ loading });
    }

    async delayedReturnToDashboard() {
        if (this._verbose) {
            console.time('[CHECKOUT] Delay and return to Dashboard');
        }
        // Ghost will restart after a change in subscription to apply the new
        // limits. Average boot time should be around 3.5sec
        return setTimeout(async () => {
            if (this._verbose) {
                console.timeEnd('[CHECKOUT] Delay and return to Dashboard');
            }

            try {
                await getPartialBillingData('users', { userId: this.state?.user?.id });
                await getPartialBillingData('invoices', { userId: this.state?.user?.id });
                await getPartialBillingData('currentSite', { data: this.state.blogs });
            } catch (error) {
                if (this._verbose) {
                    console.log('[BMA] Error receiving invoice and currentSite:', error);
                }
                // noop
            }

            await this.handleCloseModal({ reload: false });

            // When the site was in a forceUpgrade state before
            // we need to ensure to update our data after Ghost
            // has the updated config value
            if (this.state?.forceUpgradeOwner) {
                const billingData = await getForceUpgrade();

                if (this._verbose) {
                    console.log('[CHECKOUT] Updated billingData:', billingData);
                }
            }

            this.props.updateTitle('Billing and domain settings');

            return this.props.history.replace(ROUTES.DASHBOARD, {});
        }, GHOST_RESTART_DELAY_MS);
    }

    async togglePeriod(period) {
        if (this._verbose) {
            console.log('[CHECKOUT] Selected period:', period);
        }

        if (this.state.suitablePrice.billing_period !== period) {
            let buttonState = this.state.isPendingOrIncompletePrice
                ? this.buttonStates.scaRetry
                : { ...this.buttonStates.default, disabled: !this.state?.user?.cardType };

            this.setState({
                // Set both to zero to make sure the UI is updated and we don't show the proration text
                amountDue: 0,
                endingBalance: 0,
                startBalance: 0,
                proration: 0,
                error: !this.state.user?.cardType ? 'Add a card to continue' : null,
                couponLoading: true,
            });

            try {
                const productId = createProductIdFromStripeId(
                    this.state.selectedBaseProduct.stripeId,
                );
                this.props.history.push(
                    `/checkout/${productId}-${period}`,
                    this.props.history.location?.state,
                );
            } catch (error) {
                logError(error, { level: 'warning' });
                this.setState({
                    error: 'Unable to update plan period. Try again or contact support@ghost.org if the error persists.',
                });
                // Do not update the URL if productId creation fails
                return;
            }

            // Determine which coupon should be active
            const { couponId, needsUpdate } = determineSignupCouponForPeriod(
                period,
                this.state.discount,
            );

            // Create expected discount object for immediate UI update
            let expectedDiscount = this.state.discount;
            let pendingCouponUpdate = null;
            let fetchedCoupons = this.state.fetchedCoupons || {};

            if (needsUpdate && couponId) {
                // Check if we need to fetch the coupon data first, in
                // case it didn't load on initial mount
                if (!fetchedCoupons[couponId]) {
                    const result = await fetchAlternateSignupCoupon(couponId, fetchedCoupons);
                    if (result.error) {
                        // If we can't fetch the coupon, disable the button and show error
                        return this.setState((state) => ({
                            period: period,
                            suitablePrice: state.comparablePrice,
                            comparablePrice: state.suitablePrice,
                            error: DEFAULT_ERROR_MESSAGE,
                            buttonState: { ...this.buttonStates.default, disabled: true },
                            couponLoading: false,
                        }));
                    }
                    fetchedCoupons = result.fetchedCoupons;
                }

                expectedDiscount = createExpectedDiscount(
                    this.state.discount,
                    couponId,
                    fetchedCoupons,
                );
                pendingCouponUpdate = couponId;
            }

            // ensure the state is correctly set before proceeding
            await this.setState((state) => ({
                period: period,
                suitablePrice: state.comparablePrice,
                comparablePrice: state.suitablePrice,
                buttonState,
                expectedDiscount,
                pendingCouponUpdate,
                fetchedCoupons, // Update the fetched coupons
                couponLoading: false,
            }));

            if (
                this.state.suitablePrice?.stripe_price_id ===
                this.state?.currentPrice?.stripe_price_id
            ) {
                return this.setState({
                    error: 'This is your current plan. Choose a different period or plan.',
                    buttonState: { ...this.buttonStates.default, disabled: true },
                });
            } else {
                await this.setState({ buttonState });

                this.setPendingOrIncompletePrice();

                await this.getAmountDue(this.state.suitablePrice.stripe_price_id, period);
            }
        }
    }

    toggleAddonDetails(name) {
        this.setState((state) => ({
            showAddon: { ...state.showAddon, [name]: !state.showAddon[name] },
        }));
    }

    getCouponSectionHasCoupon() {
        // For incomplete subscriptions, check if the invoice has a discount
        if (this.shouldUseLatestInvoice() && this.state?.latestInvoice?.discount) {
            return true;
        }
        // Otherwise, check if the user has a coupon
        return !!this.state?.user?.coupon;
    }

    getCouponSectionDiscount() {
        // For incomplete subscriptions, use the discount from the invoice
        if (this.shouldUseLatestInvoice() && this.state?.latestInvoice?.discount) {
            return this.state.latestInvoice.discount;
        }
        // Otherwise, use the expected discount (for period switching) or the current discount
        return this.state?.expectedDiscount || this.state?.discount;
    }

    /**
     * @returns {number} the full plan price incl. addons, for display purposes
     * only - it shows the price for comparison and is not affected by invoice
     * data or prorations
     */
    getTotalAmountDisplay() {
        return getTotalPrice({
            cost: this.state.suitablePrice.cost,
            period: this.state.period,
            selectedAddons: this.state?.selectedAddons,
        });
    }

    /**
     * Updates the invoice preview and checkout button state.
     * @param {string} price
     * @param {string} period
     * @returns {Promise<number|void>}
     */
    async getAmountDue(price, period) {
        // Only use invoice data when we're on the pending/incomplete price
        if (this.shouldUseLatestInvoice()) {
            // We need to calculate any difference to the total amount to get the proration
            const amountRemaining = this.state.latestInvoice.amount_remaining / 100;
            const totalBeforeProration = this.getTotalAmountDisplay();

            // If there's a coupon, we need to subtract it from the total difference
            // to get just the credit/proration amount
            let proration = totalBeforeProration - amountRemaining;

            if (this.state?.latestInvoice?.discount?.coupon) {
                const couponAmount = getCouponDiscount({
                    coupon: this.state.latestInvoice.discount.coupon,
                    currentTotal: totalBeforeProration,
                });
                proration = proration - couponAmount;
            }

            // Proration should be negative to show as a credit
            proration = proration > 0 ? -proration : proration;

            const amountDue = this.state.latestInvoice.amount_remaining / 100;

            this.setState({
                proration,
                amountDue,
            });

            return amountDue;
        }

        // Otherwise, calculate the total
        // Preserve disabled state if there's an error
        let buttonState = this.state.isPendingOrIncompletePrice
            ? this.buttonStates.scaRetry
            : {
                  ...this.buttonStates.default,
                  disabled: !this.state?.user?.cardType || !!this.state?.error,
              };

        const totalNow = getTotalPrice({
            cost: this.state?.suitablePrice.cost,
            period,
            selectedAddons: this.state?.selectedAddons,
            coupon: this.state?.expectedDiscount?.coupon || this.state?.discount?.coupon,
        });

        if (this.state?.isTrial || this.state?.forceUpgradeOwner) {
            // there's no pro-rated credit for a trial -> subscription conversion
            // or for a lapsed trial/subscription when in forceUpgrade state
            return this.setState({ amountDue: totalNow });
        }

        this.setState({ buttonState: { ...buttonState, disabled: true } });

        const addonPrices = buildAddonPrices({
            selectedAddons: this.state?.selectedAddons,
            autoPriceAddon: this.state?.autoPriceAddon,
            autoPriceId:
                this.state?.subscription?.meta?.addons?.find(isAutoBillingAddon)?.stripe_price_id,
            period,
        });

        const invoicePreview = await getInvoicePreview({
            customerId: this.state.subscription.customer_id,
            basePrices: {
                current: this.state?.currentPrice?.stripe_price_id,
                selected: price || this.state.suitablePrice.stripe_price_id,
            },
            addonPrices,
            subscriptionId: this.state.subscription.stripe_subscription_id,
            period: period || this.state.period,
            couponId: this.state?.expectedDiscount?.coupon?.id || this.state?.discount?.coupon?.id,
        });

        const {
            amount,
            endingBalance,
            proration: previewProration,
            prorationDate,
            startBalance,
        } = invoicePreview || {};

        // Check if we should still update the state - ignore if we're now on the pending price
        // and should be using invoice data instead
        if (this.shouldUseLatestInvoice()) {
            // We're on the pending price now, so ignore this invoice preview response
            return;
        }

        if (invoicePreview?.error || !Number.isFinite(amount)) {
            return this.setState({
                amountDue: totalNow,
                endingBalance: 0,
                proration: 0,
                prorationDate: null,
                buttonState,
            });
        }

        // Basic math here because it's almost impossible to recreate the numbers
        // with what Stripe returns, mostly when switching between billing periods.

        // Normal situation: we calculate the credit from last time by simply taking
        // the ending balance and subtracting the total that we want to charge now.
        // That way the number is guaranteed to be correct and can be reconstructed
        // by the user.
        let proration = endingBalance - totalNow;

        // In a case where the whole prorated credit will be used up, the above calculation
        // doesn't work. We now need to use the amount property (which is the total that Stripe
        // will charge) and subtract it from our total. The result as negative is what makes the
        // calculation explainable.
        if (endingBalance === 0 && startBalance <= 0) {
            proration = (totalNow - amount) * -1;
        }

        if (Number.isFinite(previewProration)) {
            proration = previewProration;

            if (Number.isFinite(startBalance) && startBalance < 0) {
                proration += startBalance;
            }
        }

        this.setState({
            amountDue: amount.toFixed(2),
            endingBalance: Number.isFinite(endingBalance) ? endingBalance.toFixed(2) : 0,
            proration: proration ? proration.toFixed(2) : 0,
            prorationDate,
            buttonState,
        });
    }

    /**
     * @param {Addon} addon
     */
    handleAddonAdded(addon) {
        this.setState((state) => ({
            selectedAddons: [...state.selectedAddons, addon],
        }));
    }

    /**
     * @param {Addon} addon
     */
    handleAddonRemoved(addon) {
        this.setState((state) => ({
            selectedAddons: state.selectedAddons.filter(
                (selectedAddon) => selectedAddon.name !== addon.name,
            ),
        }));
    }

    /**
     * Submits the plan change or completes an existing payment requiring authentication.
     * @param {import('react').SyntheticEvent} event
     * @returns {Promise<unknown>}
     */
    async handleConfirmAndSubmit(event) {
        event.preventDefault();

        let deleteAutoPrice = false;
        const customerId = this.state.subscription.customer_id ?? this.state.user.stripe_token;
        const addonIds = [];

        this.setState({
            buttonState: this.buttonStates.pending,
            error: null,
            btnDisabled: true,
        });

        // when dealing with an incomplete subscription or a retry of a failed
        // SCA confirmation, we can't submit the subscription changes again and
        // need to finalise the outstanding payment.
        if (this.state?.isPendingOrIncompletePrice) {
            if (this._verbose) {
                console.log('[CHECKOUT] Pending or incomplete price, handle SCA payment');
            }
            return this.handlePaymentSCA(customerId);
        }

        if (
            this.state?.suitablePrice?.stripe_price_id === this.state?.currentPrice?.stripe_price_id
        ) {
            return this.setState({
                buttonState: this.buttonStates.fail,
                error: 'This is your current plan. Choose a different period or plan.',
                btnDisabled: false,
            });
        }

        if (this.state?.selectedAddons?.length) {
            this.state.selectedAddons.forEach((addon) => {
                addonIds.push(getPriceForPeriod(addon, this.state.period).stripe_price_id);
            });
        }

        if (this.state?.autoPriceAddon) {
            addonIds.push(
                getPriceForPeriod(this.state.autoPriceAddon, this.state.period).stripe_price_id,
            );
        } else if (!this.state?.autoPriceAddon && this.state?.billingData?.hasAutoBillingEnabled) {
            // CASE: The selected plan doesn't support auto price addons but the user
            // has this addon currently enabled.
            // We need to first remove the auto price in a separate call
            // before we can change to the new selected plan.
            deleteAutoPrice = true;
        }

        // Check if we need to update the coupon before checkout
        if (this.state.pendingCouponUpdate) {
            try {
                const couponResult = await ensureCorrectSignupCouponForPeriod({
                    selectedPeriod: this.state.period,
                    discount: this.state.originalDiscount,
                    customerId: customerId,
                });

                // Update state with the verified discount from Stripe
                this.setState({
                    discount: couponResult,
                    originalDiscount: couponResult, // Update original discount as well
                    pendingCouponUpdate: null,
                    expectedDiscount: null,
                });
            } catch (error) {
                logError(error, {
                    level: 'error',
                    tags: {
                        source: 'handleConfirmAndSubmit.couponUpdate',
                        shown_to_user: true,
                    },
                });

                return this.setState({
                    error: DEFAULT_ERROR_MESSAGE,
                    buttonState: this.buttonStates.fail,
                    btnDisabled: false,
                });
            }
        }

        // Ensure we always load fresh limit data from Daisy in case anything has changed (e. g. theme or number of staff)
        const currentSiteLimits = await getPartialBillingData('currentSiteLimits');

        // Check the current limits against the limits of the new price and product
        // and avoid changing if not suitable
        const limitsValidations = checkLimits(this.state.suitablePrice, {
            currentSiteLimits,
            product: this.state.selectedBaseProduct?.name,
            productLimits: this.state.selectedBaseProduct?.limits,
            verbose: this._verbose,
        });

        // Disable the price if any of the limits are reached (product or price)
        if (limitsValidations && limitsValidations.length >= 1) {
            return this.setState({
                buttonState: this.buttonStates.fail,
                error: limitsValidations[0].message,
                btnDisabled: false,
            });
        }

        const { paymentMethodId, error: paymentError } = await fetchPaymentMethod({ customerId });

        if (!paymentError && paymentMethodId) {
            this.setState({
                error: null,
            });

            const data = {
                customerId,
                subscriptionId: this.state.subscription.id,
                isTrial: this.state.isTrial,
                userId: this.state.user.id,
                prorationDate: this.state.prorationDate,
                paymentMethodId,
            };

            try {
                // Remove auto price first if needed
                if (deleteAutoPrice) {
                    if (this._verbose) {
                        console.log('[CHECKOUT] → Removing auto price addon');
                    }

                    data.planId = this.state?.subscription.meta.baseProduct.stripe_price_id;

                    const removedAutoPriceResponse = await changeOrActivateSubscriptionPlan(data);

                    // Wait for 2sec to ensure everything is updated in Stripe and Daisy
                    await sleep(AUTO_PRICE_REMOVAL_DELAY_MS);

                    if (
                        removedAutoPriceResponse?.error &&
                        removedAutoPriceResponse?.error?.message?.indexOf(
                            'Error fetching invoices',
                        ) < 0
                    ) {
                        return this.setState({
                            error:
                                removedAutoPriceResponse?.error?.message ||
                                'Could not remove autobilling. Switch to manual billing mode and try again.',
                        });
                    }
                }

                // Change to the new price
                data.planId = this.state.suitablePrice.stripe_price_id;
                data.addonIds = addonIds;

                const changePlanResponse = await changeOrActivateSubscriptionPlan(data);

                if (this._verbose) {
                    console.log('[CHECKOUT] → changePlanResponse', changePlanResponse);
                }

                // Ignore errors from fetching invoices, as they're not crucial to the flow and
                // can be caused by the site not being back up yet.
                if (
                    changePlanResponse?.error &&
                    changePlanResponse?.error?.message?.indexOf('Error fetching invoices') < 0
                ) {
                    return this.setState({
                        error:
                            changePlanResponse?.error?.message ||
                            'Could not process your card. Try again or contact support@ghost.org',
                        buttonState: this.buttonStates.fail,
                        btnDisabled: false,
                    });
                }

                // Changing or activating a plan requires additional payment verification (SCA/3DS) and is set into
                // a pending or incomplete state.
                if (
                    changePlanResponse.isIncomplete ||
                    changePlanResponse.isPending ||
                    changePlanResponse?.isAutoBillingGrace
                ) {
                    // Remember the selected product and period in case the user ends
                    // in a pending update state and we need to pick up where we stopped.
                    const pendingSubscription = {
                        product: this.state?.selectedBaseProduct?.name?.toLowerCase(),
                        productId: this.state?.selectedBaseProduct?.stripeId,
                        period: this.state?.period,
                        group: this.state?.selectedBaseProduct?.group,
                        priceId: this.state?.suitablePrice?.stripe_price_id,
                    };

                    try {
                        window.localStorage.setItem(
                            `gh_checkout_${customerId}`,
                            JSON.stringify(pendingSubscription),
                        );
                    } catch (error) {
                        logError(error, { level: 'warning' });
                    }

                    this.setState({
                        pendingSubscription,
                        ...changePlanResponse,
                        isPendingOrIncompletePrice: true,
                    });
                    // We need to do SCA for the payment
                    if (
                        changePlanResponse?.subscription?.meta?.paymentIntentSecret &&
                        paymentMethodId
                    ) {
                        // use the client secret delivered as meta information to confirm the card payment on-session
                        return this.handlePaymentSCA(
                            changePlanResponse?.subscription?.customer_id,
                            changePlanResponse?.subscription?.meta?.paymentIntentSecret,
                            paymentMethodId,
                        );
                    } else {
                        // handlePaymentSCA will now try to fetch the client_secret and payment method ID from Stripe directly
                        return this.handlePaymentSCA(changePlanResponse?.subscription?.customer_id);
                    }
                } else {
                    try {
                        // We're done with the checkout, don't need that anymore.
                        window.localStorage.removeItem(`gh_checkout_${customerId}`);
                    } catch (error) {
                        logError(error, { level: 'warning' });
                    }

                    return this.delayedReturnToDashboard();
                }
            } catch (error) {
                logError(error, {
                    level: 'error',
                    tags: {
                        source: 'handleConfirmAndSubmit',
                        shown_to_user: true,
                    },
                });
                return this.setState({
                    error:
                        error?.message ||
                        'Could not process your card. Try again or contact support@ghost.org',
                    buttonState: this.buttonStates.fail,
                    btnDisabled: false,
                });
            }
        } else {
            return this.setState({
                buttonState: this.buttonStates.fail,
                error: 'Card authentication failed. Try again or contact support@ghost.org',
                btnDisabled: false,
            });
        }
    }

    render() {
        if (!this.state?.loading) {
            let selectedAddons;
            let availableAddons;

            // List the add-ons for the billing summary
            if (this.state?.selectedAddons?.length) {
                selectedAddons = this.state.selectedAddons.map((addon) => {
                    const addonPrice = getPriceForPeriod(addon, this.state.period);
                    const amount =
                        this.state.period === BILLING_PERIODS.MONTH
                            ? monthlyPrice(addonPrice.cost, addonPrice.billing_period)
                            : yearlyPrice(addonPrice.cost, addonPrice.billing_period);

                    return (
                        <div className="grey" key={addon.name}>
                            <span>{addon.name}</span>
                            <span>{formatNumber(amount, { symbol: '$' })}</span>
                        </div>
                    );
                });
            }

            // List all available add-ons, but show a 'Remove' button if one of the add-ons has already been selected
            if (this.state?.availableAddons) {
                availableAddons = this.state.availableAddons.map((addon) => (
                    <AddonItem
                        key={addon.name}
                        addon={addon}
                        period={this.state.period}
                        isSelected={
                            !!this.state?.selectedAddons?.find(
                                (selectedAddon) => selectedAddon.name === addon.name,
                            )
                        }
                        hasSubdirectory={!!this.state?.hasSubdirectory}
                        showDetails={!!this.state.showAddon[addon.name]}
                        onAdd={this.handleAddonAdded}
                        onRemove={this.handleAddonRemoved}
                        onToggleDetails={this.toggleAddonDetails}
                    />
                ));
            }

            return (
                <main className="main main-med">
                    <div className="checkoutgrid" data-test-id="plan-summary">
                        <section className="checkoutgrid-main">
                            <div className="box-wrap">
                                <div className="box-label">Your plan summary</div>
                                <div className="box">
                                    <div className="plandetails">
                                        <div className="plandetails-content">
                                            <div
                                                className={`plandetails-split ${this.state.period === BILLING_PERIODS.YEAR ? 'annual' : ''}`}
                                            >
                                                <h2>
                                                    {this.state.selectedBaseProduct.name} (
                                                    {formatPeriod(
                                                        this.state.suitablePrice.billing_period,
                                                    )}
                                                    )
                                                </h2>
                                                <div>
                                                    <PeriodPrice
                                                        cost={this.state.suitablePrice.cost}
                                                        billingPeriod={
                                                            this.state.suitablePrice.billing_period
                                                        }
                                                        period={this.state.period}
                                                    />
                                                </div>
                                            </div>
                                            <p>
                                                Base plan with{' '}
                                                {this.state?.selectedBaseProduct?.features
                                                    ?.filter(
                                                        (feature) =>
                                                            feature.indexOf('Everything') < 0,
                                                    )
                                                    .join(', ')
                                                    .toLowerCase()}
                                            </p>
                                        </div>
                                        <div className="plandetails-selected">
                                            <div className="plandetails-selected-content">
                                                <strong>
                                                    {formatNumber(this.state.membersIncluded)}
                                                </strong>
                                                <span>Members included</span>
                                            </div>
                                            <Link
                                                to="/plans"
                                                className="gh-btn gh-btn-outline"
                                                disabled={this.state.btnDisabled}
                                            >
                                                Change plan
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {availableAddons?.length > 0 && (
                                <div className="box-wrap">
                                    <div className="box-label">Optional add-ons</div>
                                    <div className="box box-addon">{availableAddons}</div>
                                </div>
                            )}
                        </section>
                        <section className="checkoutgrid-side">
                            <div className="checkout-summary">
                                <div className="box-label">Billing summary</div>
                                <div className="box">
                                    <PeriodToggle
                                        period={this.state.period}
                                        disabled={
                                            this.state.btnDisabled || !this.state?.comparablePrice
                                        }
                                        onToggle={this.togglePeriod}
                                    />

                                    <div className="checkout-summary-card">
                                        <UpdateCardModal
                                            user={this.state?.user}
                                            customerId={
                                                this.state?.subscription?.customer_id ||
                                                this.state?.user?.stripe_token
                                            }
                                            isGrace={this.state?.billingData?.isGrace}
                                            handleCloseModal={this.handleCloseModal}
                                            handleNestedLoadingState={this.handleNestedLoadingState}
                                            setModalIsOpen={this.handleOpenUpdateCardModal}
                                            modalIsOpen={this.state?.updateCardModalIsOpen}
                                        />

                                        <InvoiceDetailsModal
                                            setModalIsOpen={this.handleOpenInvoiceDetailsModal}
                                            handleCloseModal={this.handleCloseModal}
                                            customerId={
                                                this.state.subscription?.customer_id ||
                                                this.state?.user?.stripe_token
                                            }
                                            invoiceDetails={this.state?.invoiceDetails}
                                            modalIsOpen={this.state?.invoiceDetailsModalIsOpen}
                                        />
                                    </div>

                                    <div className="checkout-summary-breakdown">
                                        {selectedAddons?.length > 0 && selectedAddons}

                                        <AnnualDiscount
                                            comparablePrice={this.state?.comparablePrice}
                                            suitablePrice={this.state?.suitablePrice}
                                        />
                                        <div>
                                            <span>Total per {this.state.period}</span>
                                            <strong data-test-id="checkout-total">
                                                {formatNumber(this.getTotalAmountDisplay(), {
                                                    symbol: '$',
                                                })}
                                            </strong>
                                        </div>

                                        <CouponSection
                                            hasCoupon={this.getCouponSectionHasCoupon()}
                                            discount={this.getCouponSectionDiscount()}
                                            currentTotal={this.getTotalAmountDisplay()}
                                        />

                                        {this.state?.proration < 0 && (
                                            <div
                                                className="green highlighted"
                                                data-test-id="checkout-proration-credit"
                                            >
                                                <span>Credit from last payment</span>
                                                <span>
                                                    {formatNumber(this.state.proration, {
                                                        symbol: '$',
                                                    })}
                                                </span>
                                            </div>
                                        )}
                                    </div>

                                    <div className="checkout-summary-cta">
                                        <div>
                                            <span>Amount to be billed now: </span>
                                            <span data-test-id="checkout-amount-due">
                                                {formatNumber(this.state.amountDue, {
                                                    symbol: '$',
                                                })}
                                            </span>
                                        </div>
                                        <SpinnerButton
                                            {...this.state.buttonState}
                                            data-test-btn="checkout"
                                        />

                                        {this.state.error && (
                                            <div className="form-error">
                                                {this.state.error?.match(/add a card/i) ? (
                                                    <button
                                                        className="add-card-btn"
                                                        onClick={() =>
                                                            this.handleOpenUpdateCardModal(true)
                                                        }
                                                    >
                                                        {this.state.error} &rarr;
                                                    </button>
                                                ) : (
                                                    <p className="error-msg">{this.state.error}</p>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </section>
                    </div>
                </main>
            );
        } else {
            return <Loading />;
        }
    }
}

Checkout.propTypes = {
    history: PropTypes.shape({
        push: PropTypes.func.isRequired,
        replace: PropTypes.func.isRequired,
    }).isRequired,
    updateTitle: PropTypes.func.isRequired,
    showNav: PropTypes.func.isRequired,
    // Stripe instance from CheckoutWithStripe HOC
    stripe: PropTypes.object,
};

export default Checkout;
